/**
 * Hook to derive user initials from profile data
 * Implements deterministic fallback logic for all profile states
 */

import { useMemo } from 'react';
import { UserProfile } from './types';

/**
 * Derives user initials from profile data with intelligent fallback
 * @param userProfile - User profile data with name and email fields
 * @returns Derived initials string (e.g., "JD", "J", "?", "◉")
 *
 * Rules:
 * - Loading state: returns "◉" (placeholder spinner)
 * - Error state: returns "?" (error indicator)
 * - Both firstName and lastName: returns first letter of each (e.g., "JD")
 * - Only firstName: returns first letter (e.g., "J")
 * - Only lastName: returns first letter (e.g., "D")
 * - Only email: returns first letter of email (e.g., "u" for user@example.com)
 * - No data: returns "?" (fallback)
 */
export const useInitials = (userProfile?: UserProfile): string => {
  return useMemo(() => {
    if (!userProfile) return '?';

    const { firstName, lastName, email, loadingState } = userProfile;

    // Handle loading state
    if (loadingState === 'loading') return '◉';

    // Handle error state
    if (loadingState === 'error') return '?';

    // Build initials from name fields
    let initials = '';

    const cleanFirstName = firstName?.trim() || '';
    const cleanLastName = lastName?.trim() || '';

    // If only first name is available and contains multiple words,
    // use each part's first letter (e.g., "Mary Ann" -> "MA").
    if (cleanFirstName && !cleanLastName) {
      const firstNameParts = cleanFirstName.split(/\s+/).filter(Boolean);
      if (firstNameParts.length > 1) {
        initials = firstNameParts.map((part) => part[0].toUpperCase()).join('');
      } else {
        initials = cleanFirstName[0].toUpperCase();
      }
    } else {
      if (cleanFirstName) {
        initials += cleanFirstName[0].toUpperCase();
      }

      if (cleanLastName) {
        initials += cleanLastName[0].toUpperCase();
      }
    }

    // Return initials if available
    if (initials.length > 0) return initials;

    // Fallback to email domain if no names
    if (email && email.trim()) {
      const emailLocal = email.split('@')[0];
      if (emailLocal) return emailLocal[0].toUpperCase();
    }

    // Final fallback
    return '?';
  }, [userProfile]);
};
